// src/lib/analytics.safe.ts
/**
 * Safe analytics logging facade.
 * - Gated to production builds only
 * - Uses dynamic imports so it never blocks hydration or tests
 */

export async function logEvent(name: string, params?: Record<string, unknown>) {
  if (!import.meta.env.PROD) return;
  try {
    const { logEventSafe } = await import('../analytics/events');
    await logEventSafe(name, params);
  } catch {
    // no-op: analytics should never break the app
  }
}

// Backwards-compatible alias used by some components/tests
export const log = logEvent;

export function logUserAction(action: string, context?: Record<string, unknown>): void {
  void logEvent('user_action', { action, ...context });
}

export function logPageView(page: string, metadata?: Record<string, unknown>): void {
  void logEvent('page_view', { page, ...metadata });
}

export function logError(error: string | Error, context?: Record<string, unknown>): void {
  const message = error instanceof Error ? error.message : error;
  void logEvent('error', { error: message, ...context });
}

// AI Planner specific analytics (convenience helpers)
export function logAiPlanRequest(caseId: string, caseType: string, jurisdiction: string): void {
  void logEvent('ai_plan_requested', {
    case_id: caseId,
    case_type: caseType,
    jurisdiction,
    timestamp: new Date().toISOString(),
  });
}

export function logAiPlanCacheHit(caseId: string, cacheAge: number): void {
  void logEvent('ai_plan_cached_hit', {
    case_id: caseId,
    cache_age_ms: cacheAge,
    timestamp: new Date().toISOString(),
  });
}

export function logAiPlanApplied(caseId: string, itemCount: number): void {
  void logEvent('ai_plan_applied', {
    case_id: caseId,
    item_count: itemCount,
    timestamp: new Date().toISOString(),
  });
}

export function logPlannerExportSuccess(caseId: string, format: string, itemCount: number): void {
  void logEvent('planner_export_success', {
    case_id: caseId,
    export_format: format,
    item_count: itemCount,
    timestamp: new Date().toISOString(),
  });
}